import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { HeaderNavProps } from '../types'
import { getAllServices } from '../data/services'
import { useAccessibility, useFocusTrap } from '../hooks/useAccessibility'
import { screenReader } from '../utils/accessibility'

export const HeaderNav: React.FC<HeaderNavProps> = ({
  currentService,
  isScrolled,
  showMobileMenu,
  onToggleMobileMenu
}) => {
  const services = getAllServices()
  const location = useLocation()
  const { announce, getAnimationProps } = useAccessibility()
  const mobileMenuRef = useFocusTrap(showMobileMenu)
  
  // Determine current service from URL if not provided
  const getCurrentService = () => {
    if (currentService) return currentService
    const path = location.pathname
    if (path.startsWith('/hr')) return 'hr'
    if (path.startsWith('/compliance')) return 'compliance'
    if (path.startsWith('/trade')) return 'trade'
    if (path.startsWith('/intelligence')) return 'intelligence'
    return undefined
  }
  
  const activeService = getCurrentService()

  const handleMobileMenuToggle = () => {
    onToggleMobileMenu()
    announce(showMobileMenu ? 'Menu closed' : 'Menu opened')
  }

  const handleNavigation = (serviceName: string) => {
    announce(`Navigating to ${serviceName}`)
  }

  return (
    <motion.nav
      id="main-navigation"
      role="navigation"
      aria-label="Main navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/90 backdrop-blur-md shadow-md py-2' : 'bg-transparent py-4'
      }`}
      {...getAnimationProps({
        initial: { y: -100 },
        animate: { y: 0 },
        transition: { duration: 0.5 }
      })}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <Link 
              to="/" 
              className="flex items-center space-x-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg p-1"
              aria-label="Mernaki home page"
              onClick={() => handleNavigation('home')}
            >
              <motion.div
                className="flex items-center space-x-3"
                {...getAnimationProps({
                  whileHover: { scale: 1.05 },
                  transition: { type: 'spring', stiffness: 400, damping: 10 }
                })}
              >
                <div 
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  role="img"
                  aria-label="Mernaki logo"
                >
                  <img 
                    src="/mernaki-32.png" 
                    alt="Mernaki Logo" 
                    className="w-6 h-6 object-contain"
                  />
                </div>
                <motion.span
                  className={`text-2xl font-bold transition-colors duration-300 ${
                    isScrolled
                      ? 'bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent'
                      : 'text-white drop-shadow-lg'
                  }`}
                  {...getAnimationProps({
                    initial: { opacity: 0 },
                    animate: { opacity: 1 },
                    transition: { delay: 0.2 }
                  })}
                >
                  Mernaki
                </motion.span>
              </motion.div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8" role="menubar">
            <Link
              to="/"
              className={`font-medium transition-colors duration-300 hover:text-blue-600 relative focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded px-2 py-1 ${
                location.pathname === '/'
                  ? 'text-blue-600'
                  : isScrolled
                  ? 'text-gray-700'
                  : 'text-white/90'
              }`}
              role="menuitem"
              aria-current={location.pathname === '/' ? 'page' : undefined}
              aria-label={screenReader.getNavItemDescription('Home', location.pathname === '/')}
              onClick={() => handleNavigation('Home')}
            >
              Home
              {location.pathname === '/' && (
                <motion.div
                  className="absolute -bottom-1 left-0 right-0 h-0.5 bg-blue-600"
                  layoutId="activeTab"
                  initial={false}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              )}
            </Link>
            
            {services.map((service) => (
              <div key={service.id} className="relative">
                {service.isComingSoon ? (
                  <span
                    className={`font-medium transition-colors duration-300 cursor-not-allowed px-2 py-1 rounded ${
                      isScrolled ? 'text-gray-400' : 'text-white/60'
                    }`}
                    role="menuitem"
                    aria-disabled="true"
                    aria-label={`${service.name} service coming soon`}
                  >
                    {service.name}
                    <span 
                      className="ml-1 text-xs bg-amber-400 text-white px-1 rounded"
                      aria-label="Coming soon"
                    >
                      Soon
                    </span>
                  </span>
                ) : (
                  <Link
                    to={service.baseUrl || `/${service.id}`}
                    className={`font-medium transition-colors duration-300 hover:text-blue-600 relative focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded px-2 py-1 ${
                      activeService === service.id
                        ? 'text-blue-600'
                        : isScrolled
                        ? 'text-gray-700'
                        : 'text-white/90'
                    }`}
                    role="menuitem"
                    aria-current={activeService === service.id ? 'page' : undefined}
                    aria-label={screenReader.getNavItemDescription(service.name, activeService === service.id)}
                    onClick={() => handleNavigation(service.name)}
                  >
                    {service.name}
                    {activeService === service.id && (
                      <motion.div
                        className="absolute -bottom-1 left-0 right-0 h-0.5 bg-blue-600"
                        layoutId="activeTab"
                        initial={false}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      />
                    )}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={handleMobileMenuToggle}
              className={`min-w-[44px] min-h-[44px] p-2 rounded-md transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                isScrolled
                  ? 'text-gray-700 hover:bg-gray-100'
                  : 'text-white hover:bg-white/10'
              }`}
              aria-label={showMobileMenu ? 'Close mobile menu' : 'Open mobile menu'}
              aria-expanded={showMobileMenu}
              aria-controls="mobile-menu"
              aria-haspopup="true"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {showMobileMenu ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <motion.div
          ref={mobileMenuRef}
          id="mobile-menu"
          className={`md:hidden overflow-hidden ${showMobileMenu ? 'block' : 'hidden'}`}
          role="menu"
          aria-label="Mobile navigation menu"
          aria-hidden={!showMobileMenu}
          {...getAnimationProps({
            initial: { height: 0, opacity: 0 },
            animate: { 
              height: showMobileMenu ? 'auto' : 0, 
              opacity: showMobileMenu ? 1 : 0 
            },
            transition: { duration: 0.3 }
          })}
        >
          <div className="pt-4 pb-2 space-y-2" role="none">
            <Link
              to="/"
              onClick={() => {
                handleMobileMenuToggle()
                handleNavigation('Home')
              }}
              className={`block px-3 py-2 rounded-md font-medium transition-colors duration-300 min-h-[44px] flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                location.pathname === '/'
                  ? 'bg-blue-600 text-white'
                  : isScrolled
                  ? 'text-gray-700 hover:bg-gray-100'
                  : 'text-white/90 hover:bg-white/10'
              }`}
              role="menuitem"
              aria-current={location.pathname === '/' ? 'page' : undefined}
            >
              Home
            </Link>
            
            {services.map((service) => (
              <div key={service.id} role="none">
                {service.isComingSoon ? (
                  <div 
                    className={`block px-3 py-2 rounded-md font-medium cursor-not-allowed min-h-[44px] flex items-center ${
                      isScrolled ? 'text-gray-400' : 'text-white/60'
                    }`}
                    role="menuitem"
                    aria-disabled="true"
                    aria-label={`${service.name} service coming soon`}
                  >
                    <div className="flex items-center justify-between w-full">
                      {service.name}
                      <span 
                        className="text-xs bg-amber-400 text-white px-2 py-1 rounded"
                        aria-label="Coming soon"
                      >
                        Coming Soon
                      </span>
                    </div>
                  </div>
                ) : (
                  <Link
                    to={service.baseUrl || `/${service.id}`}
                    onClick={() => {
                      handleMobileMenuToggle()
                      handleNavigation(service.name)
                    }}
                    className={`px-3 py-2 rounded-md font-medium transition-colors duration-300 min-h-[44px] flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      activeService === service.id
                        ? 'bg-blue-600 text-white'
                        : isScrolled
                        ? 'text-gray-700 hover:bg-gray-100'
                        : 'text-white/90 hover:bg-white/10'
                    }`}
                    role="menuitem"
                    aria-current={activeService === service.id ? 'page' : undefined}
                  >
                    {service.name}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.nav>
  )
}